import React, { Suspense, useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Tabs, Tab, Typography, Dialog, Chip, ListItemText, Paper } from '@mui/material';
import { DateTime } from 'luxon';

import { getSelectedAccount } from '../../../redux/selectedAccountSlice';
import { getSelectedSubscription } from '../../../redux/selectedSubscriptionSlice';
import { getSelectedBuilding } from '../../../redux/selectedBuildingSlice';

import { useGetJobSpecificationsQuery } from '../../../api/recalc/endpoints/jobSpecificationApiSlice'
import { useGetUnitsByAccountQuery } from '../../../api/recalc/endpoints/unitsApiSlice';
import { useLazyGetDataQuery } from '../../../api/recalc/endpoints/dataApiSlice';
import { useLazyGetActivationsQuery } from '../../../api/recalc/endpoints/activationsApiSlice';
import { useLazyGetBaselineDaysByIdQuery } from '../../../api/recalc/endpoints/baselineApiSlice';
import { useLazyGetOccupancyPredictionQuery, useGetOccupancyPredictionQuery } from '../../../api/subscription-manager/predictiveHeatControlSlice';
import { useUpdateSubscriptionMutation } from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';
import { useUpdateMachineLearningModelMutation } from '../../../api/subscription-manager/predictiveHeatControlSlice';
import { useGetCurrentHeatingStateOnBuildingQuery } from '../../../api/recalc/endpoints/buildingApiSlice';
import { useLazyGetAlgorithmInputSpecificationByJobTypeQuery, useLazyGetAlgorithmInputFormSpecificationByJobTypeQuery, useLazyGetOccupancyPredictionAnalysisQuery, useGetOccupancyPredictionAnalysisQuery } from '../../../api/subscription-manager/algorithmInputSlice';
import { useGetActuatorPlanQuery, useLazyGetActuatorPlanByPeriodQuery } from '../../../api/subscription-manager/predictiveHeatControlSlice';

import { useGetGatewayByIdQuery } from '../../../api/recalc/endpoints/gatewayApiSlice';
import Fade from '@mui/material/Fade';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { AppBar, IconButton, Toolbar } from '@mui/material';
import { useDispatch } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import { showSnackbar } from '../../../redux/snackbarSlice';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';
import Slide from '@mui/material/Slide';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';

// TabPanel helper function
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

const FullscreenDialog = React.lazy(() =>
  import('remote/FullscreenDialog').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const TimeRangeSelector = React.lazy(() =>
  import('remote/TimeRangeSelector').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);


const AlgorithmInputDialog = React.lazy(() =>
  import('remote/AlgorithmInputDialog').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const AlgorithmInputForm = React.lazy(() =>
  import('remote/AlgorithmInputForm').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const OccupancyPredictionForm = React.lazy(() =>
  import('remote/OccupancyPredictionForm').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});


const DataInputCard = React.lazy(() =>
  import('remote/DataInputCard').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const DataInputDetailDrawer = React.lazy(() =>
  import('remote/DataInputDetailDrawer').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);


const UserInputCard = React.lazy(() =>
  import('remote/UserInputCard').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const DataInputUpdate = React.lazy(() =>
  import('remote/DataInputUpdate').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const PredictiveHeatControlChart = React.lazy(() =>
  import('remote/PredictiveHeatControlChart').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

function formatAndEncodeTimestampForAPI(date) {
  // Convert the date to ISO format with the local timezone offset
  const isoString = DateTime.fromJSDate(date).toISO(); // Uses the local time zone by default
  return encodeURIComponent(isoString); // Encode for URL
}

function formatAndEncodeTimestampForSubscriptionManagerAPI(date) {
  return DateTime.fromJSDate(date).toUTC().toISO({ suppressMilliseconds: true });
}

const calculateAggregateType = (start, end) => {
  const startDate = DateTime.fromMillis(start);
  const endDate = DateTime.fromMillis(end);
  const durationInDays = endDate.diff(startDate, 'days').days;

  // Return "Hour" if duration is more than 3 days, otherwise "Minutes5"
  return durationInDays >= 7 ? 'Hour' : 'Minutes5';
};

const JobSubscriptionPage = () => {
  const selectedSubscription = useSelector(getSelectedSubscription);
  const selectedAccount = useSelector(getSelectedAccount);
  const selectedBuilding = useSelector(getSelectedBuilding);

  const dispatch = useDispatch(); 

  const navigate = useNavigate();

  const [timeRange, setTimeRange] = useState({
    start: DateTime.now().minus({ days: 14 }).toUTC().toMillis(), // Store UTC
    end: DateTime.now().toUTC().toMillis(), // Store UTC
  });

  const [futureTimeRange, setFutureTimeRange] = useState({  
    start: DateTime.now().toUTC().toMillis(), // Store UTC
    end: DateTime.now().plus({ days: 7 }).toUTC().toMillis(), // Store UTC
  });

  const occupancySourceExclusions = selectedSubscription?.AlgorithmInput?.predictive_occupancy_detection?.general?.occupancy_source_exclusion || [];

  const { data: units, isSuccess: isSuccessUnits } = useGetUnitsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });
  const { data: heatingState } = useGetCurrentHeatingStateOnBuildingQuery(selectedBuilding?.BuildingId, { skip: !selectedBuilding?.BuildingId });
  const { data: jobSpecifications } = useGetJobSpecificationsQuery();
  const { data: occupancyPrediction } = useGetOccupancyPredictionAnalysisQuery({jobSubscriptionId: selectedSubscription?.id, start: formatAndEncodeTimestampForAPI(new Date(futureTimeRange.start)), end: formatAndEncodeTimestampForAPI(new Date(futureTimeRange.end)), exclusions: occupancySourceExclusions}, { skip: !selectedSubscription?.id });
  const { data: gateway } = useGetGatewayByIdQuery(selectedSubscription?.UserInput?.GatewayId, { skip: !selectedSubscription?.UserInput?.GatewayId });
  const [updateSubscription, { isSuccess: isSuccessUpdateSubscription, isError: isErrorUpdateingSubscription, error: errorUpdateSubscription }] = useUpdateSubscriptionMutation();
  const [getAlgorithmInput, { isSuccess: isSuccesGetAlgorithmInput}] = useLazyGetAlgorithmInputSpecificationByJobTypeQuery();
  const [getAlgorithmFormInput, { isSuccess: isSuccessGetAlgorithmFormInput }] = useLazyGetAlgorithmInputFormSpecificationByJobTypeQuery();
  const [fetchOccupancyPredictionAnalysis, {data, isLoading: isLoadingOccupancy, isSuccess, isError, error}] = useLazyGetOccupancyPredictionAnalysisQuery();
  const [getData, { isLoading: isLoadingData }] = useLazyGetDataQuery();
  const [getActivations, { isLoading: isLoadingActivations }] = useLazyGetActivationsQuery();
  const [getActivationsByPeriod, { isLoading: isLoadingActivationsByPeriod }] = useLazyGetActuatorPlanByPeriodQuery();
  const [getBaselineDays, { isLoading: isLoadingBaselineDays }] = useLazyGetBaselineDaysByIdQuery();
  const [getOccupancyPredictions, { isLoading: isLoadingOccupancyPrediction }] = useLazyGetOccupancyPredictionQuery();
  const [specificationData, setSpecificationData] = useState(null);
  const [formSpecificationData, setFormSpecificationData] = useState(null);
  const [updateMachineLearningModel, { isSuccess: isSuccessUpdateMachineLearningModel }] = useUpdateMachineLearningModelMutation();

  const id = Number(selectedSubscription.id);
  const { data: publishedActuatorPlan } = useGetActuatorPlanQuery(
    { id },
    { skip: !id || !selectedSubscription.JobType || selectedSubscription.JobType !== 'PredictiveHeatControl' }
  );

  const [seriesData, setSeriesData] = useState([]);
  const [baselineData, setBaselineData] = useState([]);
  const [activationData, setActivationData] = useState([]);
  const [occupancyPredictionData, setOccupancyPredictionData] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dataInput, setDataInput] = useState(null);
  const [selectedDataInput, setSelectedDataInput] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [algorithmInputDialogOpen, setAlgorithmInputDialogOpen] = useState(false);
  const [modelAnalysisDialogOpen, setModelAnalysisDialogOpen] = useState(false);
  const [generatedOccupancyPlan, setGeneratedOccupancyPlan] = useState(null);
  const [isGeneratingOccupancyAnalysis, setIsGeneratingOccupancyAnalysis] = useState(false);

  const [value, setValue] = useState(0);

  const [loadingData, setLoadingData] = useState(true);
  const [loadingActivations, setLoadingActivations] = useState(false);
  const [loadingBaseline, setLoadingBaseline] = useState(false);
  const [loadingOccupancy, setLoadingOccupancy] = useState(false);
  
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };


  const fetchSpecificationData = async (jobType) => {
    try {
      const result = await getAlgorithmInput({ jobType });
      const formResult = await getAlgorithmFormInput({ jobType });
      setSpecificationData(result.data);
      setFormSpecificationData(formResult.data);
    } catch (error) {
      dispatch(showSnackbar({ message: 'Failed to fetch algorithm input specification', severity: 'error'}));
    }
  };

  useEffect(() => {
    if (selectedSubscription?.JobType && selectedSubscription?.JobType === "PredictiveHeatControl") {
      fetchSpecificationData(selectedSubscription.JobType);
    }
  }, [selectedSubscription?.JobType]);


  const handleAddDataInput = () => {
    setDialogOpen(true);
  };

  const handleClick = (dataInput) => {
    if (selectedDataInput?.UnitId === dataInput.UnitId && selectedDataInput?.UnitTypeInputId === dataInput.UnitTypeInputId) {
      setDrawerOpen(!drawerOpen);
      setSelectedDataInput(null);
    } else {
      setSelectedDataInput(dataInput);
      setDataInput(dataInput);
      setDrawerOpen(true);
    }
  };

  const handleCloseFullscreenDialog = () => {
    setDialogOpen(false);
  };

  const handleClose = () => {
    setDrawerOpen(false)
    setSelectedDataInput(null);
  };

  const handleUpdateSubscription = async (subscriptionId, data) => {
    if (data && subscriptionId) {
      try {
        const response = await updateSubscription({ id: subscriptionId, patch: data }).unwrap();
        dispatch(showSnackbar({ message: 'Updated subscription', severity: 'success', response: response }));
      } catch (error) {
        console.error('Error updating subscription:', error);
        dispatch(showSnackbar({ message: error.message || 'Error updating subscription', severity: 'error', response: error }));
      }
    }
  };

  const handleUpdateDataInput = async (subscriptionId, dataInput) => {
    try {
      // Extract relevant fields from dataInput
      const patch = {
        DataInput: dataInput.DataInput || {},
        UserInput: dataInput.UserInput || {},
        AlgorithmInput: dataInput.AlgorithmInput || {},
        DisplayName: dataInput.DisplayName || "", // Ensure DisplayName is set to a string
      };
  
      // Call the updateSubscription function with the formatted payload
      const response = await updateSubscription({ id: subscriptionId, patch }).unwrap();
  
      // Success feedback
      dispatch(showSnackbar({ message: 'Updated data input', severity: 'success', response: response }));
  
      // Close the dialog after a short delay
      setTimeout(() => {
        setDialogOpen(false);
      }, 1000);
      
    } catch (error) {
      // Enhanced error feedback
      console.error('Error updating data input:', error);
      dispatch(showSnackbar({ 
        message: error.message || 'Error updating data input', 
        severity: 'error', 
        response: error 
      }));
    }
  };

  const handleDeleteDataInput = async () => {
    try {
        // Ensure that `subscription` is correctly referenced
        const updatedDataInput = {
            ...selectedSubscription.DataInput, // Make sure to use `selectedSubscription` here
            [dataInput.dataKey]: selectedSubscription.DataInput[dataInput.dataKey].filter(
                input => !(input.UnitId === dataInput.UnitId && input.UnitTypeInputId === dataInput.UnitTypeInputId)
            ),
        };

        // Ensure that if this DataInput is required and has only one entry, it does not get removed
        if (dataInput.dataKey === 'ComfortSensorTemp' && updatedDataInput[dataInput.dataKey].length === 0) {
            dispatch(showSnackbar({ message: `You cannot remove the last entry of ${dataInput.dataKey}`, severity: 'warning' }));
            return;
        }

        const payload = {
            DataInput: updatedDataInput,
            UserInput: selectedSubscription.UserInput, // Use `selectedSubscription` here
            AlgorithmInput: selectedSubscription.AlgorithmInput, // Use `selectedSubscription` here
            DisplayName: selectedSubscription.DisplayName, // Use `selectedSubscription` here
        };

        const response = await handleUpdateDataInput(selectedSubscription.id, payload); // Use `selectedSubscription` here

        // Success feedback
       dispatch(showSnackbar({ message: 'Deleted data input', severity: 'success', response: response }));

        setTimeout(() => {
            onClose();
        }, 500);
    } catch (error) {
        dispatch(showSnackbar({ message: 'Failed to delete data input', severity: 'error', error: error }));
    }
  };

  const generateOccupancyPredictionAnalysis = async (formData) => {
    setIsGeneratingOccupancyAnalysis(true); // Start loading
  
    try {
      const result = await fetchOccupancyPredictionAnalysis({
        jobSubscriptionId: selectedSubscription.id,
        duration: formData.duration,
        filterErraticPredictions: formData.filterErraticPredictions,
        exclusions: formData.exclusions,
      });
  
      // Log the raw result before unwrapping or processing it.
  
      if (result.data) {
        setGeneratedOccupancyPlan(result.data.occupancy_plan);
      } else {
        dispatch(showSnackbar({ message: result.error.data.detail.ErrorMessage, severity: 'error' }));
        console.warn("No data in response");
      }
    } catch (error) {
      console.error("Error fetching occupancy prediction analysis:", error);
      setGeneratedOccupancyPlan(null);
    } finally {
      setIsGeneratingOccupancyAnalysis(false); // Stop loading
    }
  };

  const handleModelAnalysisDialogClose = () => {
    setModelAnalysisDialogOpen(false);
    setGeneratedOccupancyPlan(null);
  };

  const fetchOccupancyPrediction = async () => {
    try {
      const fetchedOccupancyPrediction = await getOccupancyPredictions({
        jobSubscriptionId: selectedSubscription.id,
        start: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
        end: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
      }).unwrap();
      return fetchedOccupancyPrediction || [];
    } 
    catch (error) {
      console.log(error)
      return [];
    }
  };

  const fetchActivations = async () => {
    if (selectedSubscription.JobType === 'PredictiveHeatControl' && selectedSubscription.UserInput?.ControlType === 'pct') {
      try {
          const fetchedActivationPeriods = await getActivations({
            from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
            to: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
            gatewayId: selectedSubscription.UserInput.GatewayId,
          }).unwrap();

          return fetchedActivationPeriods || [];
        } catch (error) {
          console.error('Error fetching activations:', error);
          return [];
        }
    } else if (selectedSubscription.JobType === 'PredictiveHeatControl' && selectedSubscription.UserInput?.ControlType !== 'pct') {
      try {
        const fetchedActivationPeriods = await getActivationsByPeriod({
          id: selectedSubscription.id,
          start_time: formatAndEncodeTimestampForSubscriptionManagerAPI(new Date(timeRange.start)),
          end_time: formatAndEncodeTimestampForSubscriptionManagerAPI(new Date(timeRange.end)),
        }).unwrap();

        return fetchedActivationPeriods || [];
      } catch (error) {
        console.error('Error fetching activations:', error);
        return [];
      }
    }
};

  const fetchBaseline = async () => {
    if (selectedSubscription.JobType === 'PredictiveHeatControl') {
    try {
      const fetchedBaselineDays = await getBaselineDays({
        from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
        subscriptionId: selectedSubscription?.id,
      }).unwrap();

      return fetchedBaselineDays || [];
          } catch (error) {
          console.log(error)
          return [];
        }
      };
    }

  // Fetch raw data for units
  const fetchRawDataForUnit = async (unit) => {
    try {
      const { UnitId, UnitTypeInputId, DataType } = unit;
      const fetchedData = await getData({
        from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
        to: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
        unitId: UnitId,
        datatype: DataType,
        unitTypeInputId: UnitTypeInputId,
        aggregateType: calculateAggregateType(timeRange.start, timeRange.end) || 'Hour',
      }).unwrap();

      return {
        unit,
        data: fetchedData || [],
      };
    } catch (error) {
      console.error(`Failed to fetch data for unit: ${error.message}`);
      return [];
    }
  };

  const loadingMessage = loadingData || isLoadingData ? 'Loading data...'
                    : loadingActivations || isLoadingActivations ? 'Loading activations...'
                    : loadingBaseline || isLoadingBaselineDays ? 'Loading baseline days...'
                    : loadingOccupancy || isLoadingOccupancy ? 'Loading occupancy prediction...'
                    : isLoadingActivationsByPeriod ? 'Loading activations...'
                    : null;

  useEffect(() => {
    if (isSuccessUpdateSubscription) {
      const timeout = setTimeout(() => {
        navigate(-1); // Navigate back one step in the history
      }, 1000);

      return () => clearTimeout(timeout); // Clear the timeout if the component unmounts
    }
  }, [isSuccessUpdateSubscription, navigate]); // Add navigate to the dependency array

  useEffect(() => {
    const fetchDataForUnits = async () => {
      setLoadingData(true);
      const enrichedDataInputs = Object.entries(selectedSubscription?.DataInput)?.map(([key, dataInputArray]) => {
        return dataInputArray.map(dataInput => {
          const unit = units?.find(unit => unit.UnitId === dataInput.UnitId);
          const unitSensor = unit?.UnitSensors?.find(sensor => sensor.UnitTypeInput.UnitTypeInputId === dataInput.UnitTypeInputId);
  
          return {
            ...dataInput,
            dataKey: key,
            unitSensor,
          };
        });
      }).flat();
  
      const fetchedSeriesData = await Promise.all(enrichedDataInputs.map(unit => fetchRawDataForUnit(unit)));
      setLoadingData(false); // Data fetching done

      setLoadingBaseline(true);
      const fetchedBaselineDays = await fetchBaseline();
      setLoadingBaseline(false); // Baseline fetching done

      setLoadingActivations(true);
      const fetchedActivationPeriods = await fetchActivations();
      setLoadingActivations(false); // Activations fetching done

      setLoadingOccupancy(true);
      const fetchedOccupancyPrediction = await fetchOccupancyPrediction();
      setLoadingOccupancy(false); // Occupancy fetching done
  
      const validData = fetchedSeriesData
        .filter(({ data }) => data?.length > 0) // Ensure only non-empty data arrays are considered
        .map(({ unit, data }) => ({
          unit,
          data,
        }));
      setSeriesData(validData);
      setActivationData(fetchedActivationPeriods);
      setBaselineData(fetchedBaselineDays);
      setOccupancyPredictionData(fetchedOccupancyPrediction);
    };
  
    if (units) {
      fetchDataForUnits();
    }
  }, [units, timeRange]);

  const enrichedDataInputs = Object?.entries(selectedSubscription?.DataInput || {})?.map(([key, dataInputArray], index) => {
    return dataInputArray?.map(dataInput => {
      const unit = units?.find(unit => unit.UnitId === dataInput.UnitId);
      const unitSensor = unit?.UnitSensors?.find(sensor => sensor.UnitTypeInput.UnitTypeInputId === dataInput.UnitTypeInputId);
      const relevantData = seriesData?.filter(({ unit }) => unit.UnitTypeInputId === dataInput.UnitTypeInputId && unit.UnitId === dataInput.UnitId);
      const latestDataPoint = relevantData?.length > 0 ? relevantData[relevantData.length - 1]?.data?.at(-1) : null;
      return {
        ...dataInput,
        dataKey: key,
        name: unit?.Name,
        latestDataPoint,
        unitSensor,
      };
    });
  }).flat();

  return (
    <Container maxWidth="xxl">
      <Suspense fallback={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      }>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {!loadingData && !loadingActivations && !loadingBaseline && !loadingOccupancy && !isLoadingActivationsByPeriod ? (
              <Suspense fallback={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ mt: 4 }}>Getting things ready...</Typography>
                </Box>
              }>
                <Fade in={true} timeout={500}>
                  <Box>
                  <AppBar position="static" color="transparent" elevation={0} sx={{ mb: 0 }}>
                  <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%'}}>
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                      <ListItemText 
                        primary={selectedBuilding.Name + ', ' + selectedSubscription.DisplayName} 
                        primaryTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                        secondary={
                          <>
                              {selectedSubscription.JobType === 'PredictiveHeatControl' && (
                                <Chip
                                label={heatingState ? 'Control active' : 'Control inactive'}
                                icon={<FiberManualRecordRoundedIcon color={heatingState ? 'success' : 'error' }/>}
                                size="small"
                                sx={{ mr: 1, borderRadius: 1 }} // Adds a small margin for spacing
                                />
                              )}
                                <Chip
                                  label={selectedSubscription.JobType}
                                  size="small"
                                  sx={{ mr: 1, borderRadius: 1 }} // Adds a small margin for spacing
                                />
                                <Chip
                                  label={`#${selectedSubscription.id}`}
                                  size="small"
                                  sx={{ mr: 1, borderRadius: 1 }} // Adds a small margin for spacing
                                />
                          </>
                      }
                        />
                    </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {/* IconButtons */}
                        <TimeRangeSelector timeRange={timeRange} setTimeRange={setTimeRange} />
                        <Tooltip title="Model Analysis" placement="top">
                          <IconButton sx={{ borderRadius: 1, ml: 1 }} onClick={() => setModelAnalysisDialogOpen(true)}>
                            <ModelTrainingRoundedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Algorithm Input" placement="top">
                          <IconButton sx={{ borderRadius: 1 }} onClick={() => setAlgorithmInputDialogOpen(true)}>
                            <TuneRoundedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </AppBar>
                    <PredictiveHeatControlChart
                      output={selectedSubscription.JobType === "PredictiveHeatControl" ? selectedSubscription.UserInput.Channel : null}
                      timeRange={timeRange}
                      controlType={selectedSubscription.UserInput.ControlType}
                      setTimeRange={setTimeRange}
                      rawSeriesData={seriesData}
                      baselineData={baselineData}
                      occupancyData={occupancyPredictionData}
                      activationData={activationData}

                      enrichedDataInputs={enrichedDataInputs} 
                      handleClick={handleClick}
                      userInput={selectedSubscription?.UserInput}
                      gateway={gateway}
                      subscription={selectedSubscription}
                      onAddDataInput={() => handleAddDataInput()}
                      // selected={selectedDataInput?.UnitId === enrichedDataInput.UnitId && selectedDataInput?.UnitTypeInputId === enrichedDataInput.UnitTypeInputId}
                    />
                  </Box >
                </Fade>
              </Suspense>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 4 }}>{loadingMessage}</Typography>
              </Box>
            )}
          </Grid>
            {/* <Grid item xs={12} sm={6} md={4} lg={1.5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <IconButton onClick={handleAddDataInput} sx={{ borderRadius: 1, opacity: "50%", ':hover': { opacity: "100%", transition: 'opacity 0.3s' }}}>
                    <AddRoundedIcon fontSize='large'/>
                  </IconButton>
                </Box>
              </Grid> */}
        </Grid>
        <Suspense fallback={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
            <CircularProgress />
          </Box>}>
          <DataInputDetailDrawer dataInput={dataInput} subscription={selectedSubscription} units={units} open={drawerOpen} updateDataInput={handleUpdateDataInput} removeDataInput={handleDeleteDataInput} onClose={handleClose} />
        </Suspense>
      </Suspense>
      <Suspense fallback={ 
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      }>
        <FullscreenDialog
          open={dialogOpen}
          onClose={handleCloseFullscreenDialog}
          title="Add data input"
          TransitionComponent={Transition}
        >
          <DataInputUpdate 
            subscription={selectedSubscription}
            units={units}
            jobSpecifications={jobSpecifications}
            onAdd={handleUpdateDataInput}
            onCancel={handleCloseFullscreenDialog} // Pass the onCancel handler
          />
        </FullscreenDialog>

        <FullscreenDialog
            open={algorithmInputDialogOpen}
            onClose={() => setAlgorithmInputDialogOpen(false)}
            title="Algorithm Input"
            TransitionComponent={Transition}
          >
            <Box sx={{ width: '100%' }}>
              {/* Tabs navigation */}
              <Tabs value={value} onChange={handleTabChange} aria-label="Algorithm Tabs" centered>
                <Tab label="Algorithm Input" />
                <Tab label="Advanced" />
              </Tabs>

              {/* TabPanels */}
              <TabPanel value={value} index={0}>
                <AlgorithmInputForm
                  update={handleUpdateSubscription}
                  onClose={() => setAlgorithmInputDialogOpen(false)}
                  jsonInput={selectedSubscription}
                  specificationData={formSpecificationData}
                  updateMachineLearningModel={updateMachineLearningModel}
                />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <AlgorithmInputDialog
                  update={handleUpdateSubscription}
                  onClose={() => setAlgorithmInputDialogOpen(false)}
                  jsonInput={selectedSubscription}
                  specificationData={specificationData}
                />
              </TabPanel>
            </Box>
          </FullscreenDialog>

          <FullscreenDialog
            open={modelAnalysisDialogOpen}
            onClose={handleModelAnalysisDialogClose}
            title="Model Analysis"
            TransitionComponent={Transition}
          >
            <Box sx={{ width: '100%' }}>
              <OccupancyPredictionForm subscription={selectedSubscription} actuatorPlan={publishedActuatorPlan} initialPrediction={occupancyPrediction} building={selectedBuilding} occupancySourceExclusions={occupancySourceExclusions} generateOccupancyPredictionAnalysis={generateOccupancyPredictionAnalysis} setGeneratedOccupancyPlan={() => setGeneratedOccupancyPlan(null)} generatedOccupancyPlan={generatedOccupancyPlan} isGenerating={isGeneratingOccupancyAnalysis}/>
            </Box>
          </FullscreenDialog>
      </Suspense>
    </Container>
  );
}

export default JobSubscriptionPage;